import { Route, Routes } from "react-router-dom";

import DealerDashboard from "../components/dealer/DealerDashboard";
import { AgentsPage } from "../features/agents/AgentsPage";
import { DashboardPage } from "../features/dashboard/DashboardPage";
import { FollowUpsPage } from "../features/followups/FollowUpsPage";
import { InventoryPage } from "../features/inventory/InventoryPage";
import { LeadsPage } from "../features/leads/LeadsPage";
import { ReportsPage } from "../features/reports/ReportsPage";
import { SettingsPage } from "../features/settings/SettingsPage";
import { SupportPage } from "../features/support/SupportPage";

import { INITIAL_REPORT_FILTERS } from "../lib/constants";
import { ROUTE_PATHS, isLeadsContentLabel } from "./navigation";

export function AppContent({
  activeSidebarItem,
  activeTopNav,
  applyReportFilters,
  agents,
  context,
  currentUser,
  exportingReport,
  followUps,
  geographyData,
  integrationDiagnostics,
  leadsPageProps,
  loadContext,
  loadIntegrationDiagnostics,
  loading,
  loadingReports,
  openAgentAdmin,
  openBulkReassign,
  openBulkUpload,
  refreshSelectedLead,
  reportData,
  reportDraftFilters,
  reportFilters,
  roleScope,
  selectedLead,
  selectedLeadId,
  setError,
  setJourneyModalOpen,
  setNotice,
  setPendingCallLeadId,
  setPendingCreateLeadAgent,
  setReportDraftFilters,
  setReportFilters,
  setSelectedLead,
  setSelectedLeadId,
  setActiveReportTab,
  setExportingReport,
  triggerReportSync,
  updateReportDraftFilter,
  loadWorkspace,
  activeReportTab,
}) {
  if (currentUser?.role === "dealer") {
    return (
      <Routes>
        <Route path="*" element={<DealerDashboard view={activeSidebarItem} />} />
      </Routes>
    );
  }

  return (
    <>
      <Routes>
        <Route
          path={ROUTE_PATHS.salesAgents}
          element={
            <AgentsPage
              backendGeographies={context.geographies}
              currentUser={currentUser}
              onAddAgent={openAgentAdmin}
              onAssignLead={(agent) => {
                setPendingCreateLeadAgent(agent);
              }}
              onBulkAssign={openBulkReassign}
              onBulkReassign={openBulkReassign}
              onUploadLeads={openBulkUpload}
            />
          }
        />
        <Route
          path={ROUTE_PATHS.salesFollowUps}
          element={
            <FollowUpsPage
              followUps={followUps}
              roleScope={roleScope}
              onLogCall={(leadId) => {
                setPendingCallLeadId(String(leadId));
              }}
              onMarkComplete={async (followUpId) => {
                try {
                  setError("");
                  const { fetchJson: fj } = await import("../lib/fetchJson");
                  await fj(`/api/crm/follow-ups/${followUpId}/`, {
                    method: "PATCH",
                    body: JSON.stringify({ status: "completed" }),
                  });
                  setNotice("Follow-up marked as completed.");
                  await loadWorkspace();
                  if (selectedLeadId) {
                    await refreshSelectedLead(selectedLeadId);
                  }
                } catch (requestError) {
                  setError(requestError.message);
                }
              }}
              onOpenJourney={async (leadId) => {
                try {
                  setError("");
                  if (!leadId) return;
                  const detail =
                    String(selectedLeadId) === String(leadId) && selectedLead
                      ? selectedLead
                      : await refreshSelectedLead(leadId);
                  setSelectedLeadId(String(leadId));
                  setSelectedLead(detail);
                  setJourneyModalOpen(true);
                } catch (requestError) {
                  setError(requestError.message);
                }
              }}
            />
          }
        />
        <Route
          path={ROUTE_PATHS.salesReports}
          element={
            <ReportsPage
              activeReportTab={activeReportTab}
              agents={agents}
              context={context}
              currentUser={currentUser}
              exportingReport={exportingReport}
              geographyData={geographyData}
              loadingReports={loadingReports}
              reportData={reportData}
              reportDraftFilters={reportDraftFilters}
              reportFilters={reportFilters}
              roleScope={roleScope}
              setActiveReportTab={setActiveReportTab}
              setExportingReport={setExportingReport}
              setNotice={setNotice}
              setReportDraftFilters={setReportDraftFilters}
              setReportFilters={setReportFilters}
              updateReportDraftFilter={updateReportDraftFilter}
            />
          }
        />
        <Route
          path={ROUTE_PATHS.salesDashboard}
          element={
            <DashboardPage
              agents={agents}
              context={context}
              loadingReports={loadingReports}
              reportData={reportData}
              reportDraftFilters={reportDraftFilters}
              roleScope={roleScope}
              onFiltersChange={(patch) => {
                applyReportFilters(patch);
              }}
              onReportFilterReset={() => {
                const todayStr = new Date().toISOString().slice(0, 10);
                const todayFilters = {
                  ...INITIAL_REPORT_FILTERS,
                  dateFrom: todayStr,
                  dateTo: todayStr,
                };
                setReportDraftFilters(todayFilters);
                setReportFilters(todayFilters);
                triggerReportSync();
              }}
              onSyncNow={triggerReportSync}
            />
          }
        />
        <Route path={ROUTE_PATHS.inventory} element={<InventoryPage />} />
        <Route
          path={ROUTE_PATHS.admin}
          element={
            <SettingsPage
              currentUser={currentUser}
              integrationDiagnostics={integrationDiagnostics}
              onContextChange={() => loadContext().catch(() => {})}
              onRefreshIntegrationDiagnostics={loadIntegrationDiagnostics}
              roleScope={roleScope}
            />
          }
        />
        <Route path={ROUTE_PATHS.support} element={<SupportPage />} />
      </Routes>

      <LeadsPage
        {...leadsPageProps}
        showContent={activeTopNav === "Sales" && isLeadsContentLabel(activeSidebarItem)}
      />
    </>
  );
}

export default AppContent;
